import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/back-services/auth.service';
import { DxcDialogService } from '../../../modules/primitives/dxc/dialog/dxc-dialog.service';
import type { DxcPending } from '../../../modules/primitives/dxc/dialog/dxc-dialog.service';
import { ToastService } from '../../../modules/primitives/toasts/toast.service';
import { ActiveClock } from './active-clock';
import { setTelemetryApiObserver } from './telemetry-bridge';
import type { TelemetryApiCall, TelemetryApiObserver } from './telemetry-bridge';
import { describeClick } from './telemetry-click';
import { TelemetryQueue, fetchTelemetrySend } from './telemetry-queue';
import type { TelemetryBatchMeta } from './telemetry-queue';
import { describeRoute } from './telemetry-route';
import type { RouteDescription } from './telemetry-route';
import { TelemetrySessionStore, newTelemetryId, readAnonId } from './telemetry-session';
import type { TelemetrySessionState } from './telemetry-session';
import { scrubCopy, scrubText } from './telemetry-text';
import { describeDialogAnswer, describeDialogOpen } from './telemetry-ui';
import { TELEMETRY_LIMITS } from './telemetry.types';
import type { TelemetryEventInput } from './telemetry.types';

const OPT_OUT_KEY = 'dex.telemetry.optOut';

/** Llamadas que la app repite sola cada pocos segundos: su éxito no dice nada y llenaría la tabla. Sus fallos sí se guardan. */
const POLLING_CALLS = new Set(['notifications.notifications', 'notifications.unsyncnotifications']);

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

interface CurrentRoute {
    route: RouteDescription;
    clock: ActiveClock;
    enteredAt: number;
}

/**
 * Telemetría de uso del frontend: qué rutas se visitan y cuánto tiempo ACTIVO se pasa en cada una,
 * dónde se hace clic, qué llamadas al backend se hacen y cuáles fallan, y qué errores se escapan.
 * Lo envía por lotes a `/telemetry/track`; quién es el usuario lo decide el servidor con el token.
 *
 * Lo que nunca sale de aquí: lo que el usuario escribe, los argumentos de una llamada, la consulta
 * de una URL, ni el texto de una opción de un desplegable (puede ser el nombre de un cliente).
 *
 * Se enciende con `environment.telemetry.enabled`, y cada navegador puede apagarlo con
 * `localStorage['dex.telemetry.optOut'] = '1'`. Apagada, no escucha nada ni hace una sola petición.
 * Los escuchadores corren fuera de la zona de Angular: un `pointermove` no puede disparar la
 * detección de cambios.
 */
@Injectable({
    providedIn: 'root'
})
export class TelemetryService implements TelemetryApiObserver, OnDestroy {
    private readonly enabled = !!environment.telemetry?.enabled && !this.optedOut();
    private readonly traceHeaders = !!environment.telemetry?.traceHeaders;

    private readonly sessions = new TelemetrySessionStore(this.storage('session'), newTelemetryId);
    private readonly queue = new TelemetryQueue(fetchTelemetrySend(`${environment.srcUrl}/telemetry/track`), () => this.halt());
    private anonId?: string;

    private current?: CurrentRoute;
    private lastInput = Date.now();
    private idle = false;
    private lastToken: string | null = null;
    private errorsThisPage = 0;
    private lastErrorKey = '';

    private started = false;
    private readonly subscriptions: Subscription[] = [];
    private readonly teardown: (() => void)[] = [];

    /** Diálogos ya observados: el mismo objeto pendiente no se envuelve dos veces. */
    private readonly seenDialogs = new WeakSet<object>();
    private lastToastId = 0;

    constructor (
        private router: Router,
        private zone: NgZone,
        private authService: AuthService,
        private dialogs: DxcDialogService,
        private toasts: ToastService,
    ) {}

    /** Arranca la captura. Lo llama el inicializador de la app; llamarlo dos veces no hace nada. */
    start (): void {
        if (!this.enabled || this.started) return;
        this.started = true;

        this.anonId = readAnonId(this.storage('local'), newTelemetryId);
        setTelemetryApiObserver(this);

        this.subscriptions.push(
            this.router.events
                .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe(event => this.onNavigation(event)),
            this.authService.token.onClear(() => this.onLogout()),
            this.dialogs.$pending.subscribe(pending => this.onDialog(pending)),
            this.toasts.$toasts.subscribe(toasts => this.onToasts(toasts)),
        );

        this.zone.runOutsideAngular(() => {
            this.listen(document, 'click', event => this.onClick(event), { capture: true, passive: true });
            for (const type of INPUT_EVENTS) this.listen(document, type, () => this.onInput(), { capture: true, passive: true });
            this.listen(document, 'visibilitychange', () => this.onVisibilityChange());
            this.listen(window, 'pagehide', () => this.onPageHide());
            this.listen(window, 'pageshow', event => (event as PageTransitionEvent).persisted && this.onPageRestored());
            this.listen(window, 'error', event => this.captureError((event as ErrorEvent).error ?? (event as ErrorEvent).message, 'window'));
            this.listen(window, 'unhandledrejection', event => this.captureError((event as PromiseRejectionEvent).reason, 'promise'));

            const timer = setInterval(() => this.onTick(), TELEMETRY_LIMITS.flushIntervalMs);
            this.teardown.push(() => clearInterval(timer));
        });
    }

    ngOnDestroy (): void {
        this.halt();
    }

    /**
     * Deja de capturar en lo que queda de la página: no escucha, no mide y no envía. Lo pide el
     * servidor cuando la persona se opuso a que se registre su uso (oposición ARCO).
     */
    private halt (): void {
        this.started = false;
        setTelemetryApiObserver(null);
        this.subscriptions.splice(0).forEach(subscription => subscription.unsubscribe());
        this.teardown.splice(0).forEach(dispose => dispose());
    }

    /** Registra un evento. Es la puerta para lo que no se captura solo: pasos de un flujo, diálogos, el asistente. */
    track (input: TelemetryEventInput, at = Date.now()): void {
        if (!this.started) return;

        const state = this.ensureSession(at);
        this.enqueue(state, { ...input, route: input.route ?? this.current?.route.pattern }, at);
        if (this.queue.size >= TELEMETRY_LIMITS.maxBatch) this.flush();
    }

    /** Un error sin capturar. Lo llaman el `ErrorHandler` de Angular y los escuchadores de `window`. */
    captureError (error: unknown, source: 'angular' | 'window' | 'promise'): void {
        if (!this.started || this.errorsThisPage >= TELEMETRY_LIMITS.maxErrorsPerPage) return;

        const err = error instanceof Error ? error : null;
        const message = scrubText(err?.message ?? String(error ?? ''), 200);
        const name = err?.name || 'Error';

        // El mismo error llega a la vez por la zona de Angular y por `window`: se cuenta una vez.
        const key = `${name}:${message}`;
        if (key === this.lastErrorKey) return;
        this.lastErrorKey = key;
        this.errorsThisPage++;

        const stack = (err?.stack ?? '').split('\n').slice(1, 5)
            .map(frame => scrubText(frame.replace(/https?:\/\/[^/\s)]+/g, ''), 200))
            .filter(Boolean);

        this.track({ type: 'error', name, props: { message, source, ...(stack.length ? { stack } : {}) } });
    }

    beginApiCall (name: string): TelemetryApiCall | null {
        if (!this.started) return null;

        const traceId = newTelemetryId();
        const startedAt = performance.now();
        const sessionId = this.sessions.lastKnown()?.id;

        return {
            headers: this.traceHeaders && sessionId ? { 'x-dex-trace': traceId, 'x-dex-session': sessionId } : {},
            end: ({ ok, status, code }) => {
                if (ok && POLLING_CALLS.has(name.toLowerCase())) return;
                this.track({
                    type: 'api',
                    name,
                    durationMs: Math.round(performance.now() - startedAt),
                    traceId,
                    props: { ok, ...(status ? { status } : {}), ...(code ? { code: scrubText(code, 80) } : {}) },
                });
            },
        };
    }

    // ── Diálogos y notificaciones ───────────────────────────────────────────────

    /**
     * Un diálogo del sistema que se abre. Se envuelve su `resolve` —el servicio de diálogos lo llama
     * con la respuesta, venga del botón, de Esc o del velo— para registrar también qué se contestó
     * y cuánto se tardó, sin tocar el servicio ni a quien abrió el diálogo.
     */
    private onDialog (pending: DxcPending | null): void {
        if (!pending || this.seenDialogs.has(pending)) return;
        this.seenDialogs.add(pending);

        const { name, props } = describeDialogOpen(pending as any);
        const openedAt = Date.now();
        this.track({ type: 'dialog', name, props: { ...props, phase: 'open' } }, openedAt);

        const target = pending as { resolve: (value: unknown) => void };
        const resolve = target.resolve;
        target.resolve = (value: unknown) => {
            try {
                this.track({ type: 'dialog', name, durationMs: Date.now() - openedAt, props: { ...props, phase: 'answer', answer: describeDialogAnswer(pending as any, value) } });
            } catch { /* medir no puede impedir que el diálogo conteste */ }
            resolve(value);
        };
    }

    /** Notificaciones nuevas desde la última vez: su tipo y su mensaje sin datos interpolados. */
    private onToasts (toasts: { id: number, kind: string, message: string }[]): void {
        for (const toast of toasts) {
            if (toast.id <= this.lastToastId) continue;
            this.lastToastId = toast.id;
            this.track({ type: 'toast', name: scrubCopy(toast.message) || toast.kind, props: { kind: toast.kind } });
        }
    }

    // ── Rutas y tiempo activo ───────────────────────────────────────────────────

    private onNavigation (event: NavigationEnd): void {
        const route = describeRoute(this.router.routerState.snapshot.root, event.urlAfterRedirects);
        const previous = this.current;

        // Cambiar solo la consulta (un filtro, una pestaña con `?tab=`) no es otra pantalla.
        if (previous && previous.route.pattern === route.pattern && sameParams(previous.route.params, route.params)) return;

        // Si la sesión caducó mientras tanto, cerrarla ya dejó la ruta y la reabrió en la nueva:
        // se sale de ESA, no de la vieja, o el tiempo se contaría dos veces.
        const now = Date.now();
        this.ensureSession(now);
        if (this.current) this.leave(this.current, now, { to: route.pattern });

        this.current = { route, clock: new ActiveClock(now, this.isActive()), enteredAt: now };
        this.track({ type: 'route', name: 'enter', route: route.pattern, routeParams: route.params, ...(previous ? { props: { from: previous.route.pattern } } : {}) }, now);
    }

    private leave (current: CurrentRoute, at: number, props: Record<string, string | boolean> = {}): void {
        this.track({
            type: 'route',
            name: 'leave',
            route: current.route.pattern,
            routeParams: current.route.params,
            durationMs: current.clock.read(at),
            props: { wallMs: at - current.enteredAt, ...props },
        }, at);
    }

    private onInput (): void {
        const now = Date.now();
        this.lastInput = now;
        if (!this.idle) return;

        this.idle = false;
        if (document.visibilityState === 'visible') this.current?.clock.resume(now);
        this.track({ type: 'visibility', name: 'active' }, now);
    }

    private onVisibilityChange (): void {
        const now = Date.now();
        if (document.visibilityState === 'hidden') {
            this.current?.clock.pause(now);
            this.track({ type: 'visibility', name: 'hidden' }, now);
            this.flush({ keepalive: true });
        } else {
            this.lastInput = now;
            this.idle = false;
            this.current?.clock.resume(now);
            this.track({ type: 'visibility', name: 'visible' }, now);
        }
    }

    /**
     * La pestaña se va: cierre, recarga o salida a otro sitio. No se distingue cuál, así que la
     * sesión NO se da por terminada aquí —una recarga la continúa—; su fin se deduce de `lastSeenAt`.
     */
    private onPageHide (): void {
        const now = Date.now();
        if (this.current) this.leave(this.current, now, { unload: true });
        this.track({ type: 'visibility', name: 'unload' }, now);
        this.flush({ keepalive: true });
    }

    /** Volvió de la caché de atrás/adelante: la página no se recargó, pero la ruta se había dado por dejada. */
    private onPageRestored (): void {
        const now = Date.now();
        if (!this.current) return;
        this.current = { ...this.current, clock: new ActiveClock(now, this.isActive()), enteredAt: now };
        this.track({ type: 'route', name: 'enter', route: this.current.route.pattern, routeParams: this.current.route.params, props: { restored: true } }, now);
    }

    private onTick (): void {
        const now = Date.now();
        const idleSince = this.lastInput + TELEMETRY_LIMITS.idleAfterMs;

        if (!this.idle && document.visibilityState === 'visible' && now >= idleSince) {
            this.idle = true;
            this.current?.clock.pause(idleSince);
            this.track({ type: 'visibility', name: 'idle' }, idleSince);
        }

        this.flush();
    }

    private onClick (event: Event): void {
        const click = describeClick(event.target);
        if (click) this.track({ type: 'click', name: click.name, props: click.props });
    }

    // ── Sesión ──────────────────────────────────────────────────────────────────

    /**
     * La sesión a la que va el próximo evento. Si la anterior caducó por inactividad, la cierra con
     * SU id —la ruta que quedó abierta y el fin— y abre otra; la ruta actual se vuelve a abrir en la
     * nueva, para que su línea de tiempo empiece con contexto.
     */
    private ensureSession (at: number): TelemetrySessionState {
        let touch = this.sessions.touch(at);
        const expired = touch.expired;

        if (expired) {
            this.closeExpired(expired);
            touch = this.sessions.touch(at);
        }
        if (!touch.started) return touch.state;

        this.enqueue(touch.state, { type: 'session', name: 'start' }, at);
        if (expired && this.current) {
            this.current = { ...this.current, clock: new ActiveClock(at, this.isActive()), enteredAt: at };
            this.enqueue(touch.state, { type: 'route', name: 'enter', route: this.current.route.pattern, routeParams: this.current.route.params }, at);
        }
        return touch.state;
    }

    private closeExpired (expired: TelemetrySessionState): void {
        const at = expired.lastActivity;
        if (this.current) {
            this.enqueue(expired, {
                type: 'route', name: 'leave', route: this.current.route.pattern, routeParams: this.current.route.params,
                durationMs: this.current.clock.read(at), props: { wallMs: at - this.current.enteredAt },
            }, at);
        }
        this.enqueue(expired, { type: 'session', name: 'end', props: { reason: 'idle' } }, at);
        this.sessions.forget();
    }

    /**
     * Cerrar sesión. Otro servicio vacía el almacenamiento en este mismo momento, así que se trabaja
     * con la sesión en memoria, sin volver a escribirla —si se escribiera, la sesión sobreviviría al
     * logout—, y se envía con el último token conocido antes de que la página recargue.
     */
    private onLogout (): void {
        const last = this.sessions.lastKnown();
        if (!last) return;

        const now = Date.now();
        const pending: TelemetryEventInput[] = [];
        if (this.current) {
            pending.push({
                type: 'route', name: 'leave', route: this.current.route.pattern, routeParams: this.current.route.params,
                durationMs: this.current.clock.read(now), props: { wallMs: now - this.current.enteredAt },
            });
        }
        pending.push({ type: 'session', name: 'end', props: { reason: 'logout' } });

        let seq = last.seq;
        for (const input of pending) this.queue.push(last.id, { ...input, seq: seq++, ts: now });

        this.sessions.forget();
        this.current = undefined;
        this.flush({ keepalive: true });
        this.lastToken = null;
    }

    private enqueue (state: TelemetrySessionState, input: TelemetryEventInput, at: number): void {
        const seq = this.sessions.next(state, at);
        this.queue.push(state.id, { ...input, seq, ts: at }, this.meta(state));
    }

    private meta (state: TelemetrySessionState): TelemetryBatchMeta {
        return {
            anonId:     this.anonId,
            startedAt:  state.startedAt,
            entryRoute: this.current?.route.pattern,
            userAgent:  navigator.userAgent,
            viewport:   `${window.innerWidth}x${window.innerHeight}`,
        };
    }

    // ── Envío ───────────────────────────────────────────────────────────────────

    private flush ({ keepalive = false } = {}): void {
        const token = this.readToken();
        this.zone.runOutsideAngular(() => { this.queue.flush(token, { keepalive }); });
    }

    private readToken (): string | null {
        try {
            const token = localStorage.getItem('token');
            if (token) this.lastToken = token;
        } catch { /* sin almacenamiento: queda el último conocido */ }
        return this.lastToken;
    }

    // ── Utilidades ──────────────────────────────────────────────────────────────

    private isActive (): boolean {
        return document.visibilityState === 'visible' && !this.idle;
    }

    private listen (target: EventTarget, type: string, handler: (event: Event) => unknown, options?: AddEventListenerOptions): void {
        const safe = (event: Event) => { try { handler(event); } catch { /* medir no puede romper la página */ } };
        target.addEventListener(type, safe, options);
        this.teardown.push(() => target.removeEventListener(type, safe, options));
    }

    private storage (kind: 'session' | 'local'): Storage | null {
        try {
            return kind === 'session' ? window.sessionStorage : window.localStorage;
        } catch {
            return null;
        }
    }

    private optedOut (): boolean {
        try {
            return localStorage.getItem(OPT_OUT_KEY) === '1';
        } catch {
            return false;
        }
    }
}

function sameParams (a?: Record<string, string>, b?: Record<string, string>): boolean {
    return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}
